/**
 * Contratos globais de commerce (root `PixelConfig.commerce`).
 * Não pertencem a conversion — representam o contexto comercial da página/fluxo.
 */

export type ProductType =
  | 'main'
  | 'order_bump'
  | 'upsell'
  | 'downsell'
  | 'cross_sell'
  | 'addon'
  | 'subscription'
  | 'custom'
  | 'other';

export type CommerceProduct = {
  id: string;
  name?: string;
  sku?: string;
  type?: ProductType;
  price?: number;
  currency?: string;
  quantity?: number;
  category?: string;
  variant?: string;
  brand?: string;
  metadata?: Record<string, unknown>;
};

export type CommerceCartItem = {
  productId: string;
  quantity?: number;
  price?: number;
  currency?: string;
  selected?: boolean;
  metadata?: Record<string, unknown>;
};

export type CommerceCartMode =
  | 'landing_page'
  | 'single_product'
  | 'multi_product'
  | 'checkout'
  | 'upsell'
  | 'custom'
  | 'cart'
  | 'funnel';

export type CommerceCalculateValueFrom =
  | 'cart'
  | 'event'
  | 'manual'
  | 'catalog';

export type CommerceEnrichmentSource =
  | 'cart'
  | 'event'
  | 'event_or_cart'
  | 'catalog';

export type CommerceEventEnrichmentConfig = {
  enabled: boolean;
  source?: CommerceEnrichmentSource;
};

export type CommerceEnrichmentConfig = {
  pageView?: CommerceEventEnrichmentConfig;
  viewContent?: CommerceEventEnrichmentConfig;
  lead?: CommerceEventEnrichmentConfig;
  addToCart?: CommerceEventEnrichmentConfig;
  removeFromCart?: CommerceEventEnrichmentConfig;
  initiateCheckout?: CommerceEventEnrichmentConfig;
  addPaymentInfo?: CommerceEventEnrichmentConfig;
  purchase?: CommerceEventEnrichmentConfig;
};

export type ProductCatalogConfig = {
  products?: CommerceProduct[];
};

export type CommerceCartConfig = {
  mode?: CommerceCartMode;
  /** Seed inicial do carrinho — não é estado vivo. */
  initialItems?: CommerceCartItem[];
  allowDynamicItems?: boolean;
  calculateValueFrom?: CommerceCalculateValueFrom;
  metadata?: Record<string, unknown>;
};

export type CommerceMappingsConfig = {
  attributes?: Record<string, string[]>;
  fields?: Record<string, string[]>;
};

export const CUSTOMER_FIELD_KEYS = [
  'email',
  'phone',
  'name',
  'firstName',
  'lastName',
  'address',
  'address2',
  'city',
  'state',
  'postalCode',
  'country',
] as const;

export type CustomerFieldKey = (typeof CUSTOMER_FIELD_KEYS)[number];

export type CustomerFieldSelectorInput = string | readonly string[];

/**
 * Seletores extras de customer. Sempre somam aos defaults — nunca substituem.
 * Aceita name/id (`user_email`) ou CSS (`#contato-email`, `[data-cliente-email]`).
 */
export type CommerceCustomerConfig = {
  fields?: Partial<Record<CustomerFieldKey, CustomerFieldSelectorInput>>;
};

export type CommerceConfig = {
  enabled?: boolean;
  currency?: string;
  catalog?: ProductCatalogConfig;
  cart?: CommerceCartConfig;
  mappings?: CommerceMappingsConfig;
  customer?: CommerceCustomerConfig;
  enrichment?: CommerceEnrichmentConfig;
};

export const DEFAULT_COMMERCE_ENRICHMENT: Required<CommerceEnrichmentConfig> = {
  pageView: { enabled: true, source: 'cart' },
  viewContent: { enabled: true, source: 'event_or_cart' },
  lead: { enabled: false, source: 'event_or_cart' },
  addToCart: { enabled: true, source: 'event_or_cart' },
  removeFromCart: { enabled: true, source: 'event_or_cart' },
  initiateCheckout: { enabled: true, source: 'cart' },
  addPaymentInfo: { enabled: true, source: 'cart' },
  purchase: { enabled: true, source: 'cart' },
};

/**
 * Commerce on by default so catalog/cart enrichment runs when products exist.
 * Empty catalog / empty cart is a no-op.
 */
export const DEFAULT_COMMERCE_CONFIG: CommerceConfig = {
  enabled: true,
  currency: 'BRL',
  catalog: {
    products: [],
  },
  cart: {
    mode: 'custom',
    initialItems: [],
    allowDynamicItems: true,
    calculateValueFrom: 'cart',
  },
  mappings: {
    attributes: {},
    fields: {},
  },
  customer: {
    fields: {},
  },
  enrichment: { ...DEFAULT_COMMERCE_ENRICHMENT },
};

function selectorList(
  value: CustomerFieldSelectorInput | undefined
): string[] {
  if (!value) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.map(item => String(item).trim()).filter(Boolean);
}

/**
 * Junta `commerce.customer.fields` com extras de `commerce.mappings.fields`.
 * O extrator mescla o resultado com os defaults de cada campo.
 */
export function resolveCustomerFieldMappings(
  customer?: CommerceCustomerConfig | null,
  legacyFields?: Record<string, string | readonly string[]> | null
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const key of CUSTOMER_FIELD_KEYS) {
    const extras = [
      ...selectorList(customer?.fields?.[key]),
      ...selectorList(legacyFields?.[key]),
    ];
    if (extras.length > 0) {
      result[key] = extras;
    }
  }

  return result;
}

/** Normaliza calculateValueFrom (inclui aliases legado em runtime). */
export function normalizeCalculateValueFrom(
  value: CommerceCalculateValueFrom | string | undefined
): 'cart' | 'event' | 'manual' | 'catalog' {
  switch (value) {
    case 'event':
    case 'event_items':
      return 'event';
    case 'manual':
      return 'manual';
    case 'catalog':
      return 'catalog';
    case 'cart':
    case 'selected_items':
    default:
      return 'cart';
  }
}
